import _ from 'lodash';
import tinycolor from 'tinycolor2';
import { CAPSULE_TYPES } from '@/chart/chart.constants';
import { getScaledFontSize } from '@/core/html.utilities';
import { CAPSULE_TIME_LANE_BUFFER, LANE_BUFFER } from '@/trend/trendViewer/trendViewer.constants';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { LANE_LABEL_HEIGHT_REM } from '@/utilities/chartLanes.constants';
import { computeOverlayLayoutForLevel, getOverlayOffset } from '@/utilities/chartOverlay.utilities';
import { getCertainId } from '@/utilities/id.utilities';
import type { SeriesLineOptions } from 'highcharts';
import type {
  AdjustableConditions,
  ChartLaneOptions,
  ConditionAxisAndOffsets,
  ConditionLaneData,
} from '@/utilities/chart.types';

interface LaneValues {
  offset: number;
  height: number;
  buffer: number;
  conditionHeight?: number;
  signalHeight?: number;
}

const EXPAND_THRESHOLD = 7;

/**
 * A class to manage most lane operations
 */
class ChartLanes {
  conditionLaneHeights = new Map<number, { height: number }>();
  totalConditionLaneHeight = 0;
  private _extraConditionLaneHeight = 0;
  private allLaneData: ConditionLaneData = new Map();

  /** Conditions with capsule lineWidths adjusted to fill signal lane */
  conditionsInSignalLane: AdjustableConditions = new Map();
  /** Conditions with capsule lineWidths adjusted to fill chart */
  expandedConditions: AdjustableConditions = new Map();

  get extraConditionLaneHeight() {
    return this._extraConditionLaneHeight;
  }

  updateConditionLaneHeights = (allLaneData: ConditionLaneData): void => {
    this.totalConditionLaneHeight = 0;
    this.conditionLaneHeights = new Map();
    this.allLaneData = allLaneData;
    allLaneData.forEach((laneData, lane) => {
      this.conditionLaneHeights.set(lane, { height: laneData.nextOffset });
      this.totalConditionLaneHeight += laneData.nextOffset;
    });
  };

  /**
   * Get fixed lane height for signals
   */
  getSignalLaneHeight = ({ items, chart, capsuleLaneHeight, isCapsuleTime }: ChartLaneOptions): number => {
    const lanes = this.getDisplayedLanes(items);
    const numberOfSignalLanes = this.getDisplayedLanes(items, true).length;
    const displayHeight = this.getChartSeriesDisplayHeight({ chart, capsuleLaneHeight });

    if (numberOfSignalLanes > 0) {
      const extraBuffer = this.hasSignalsInLane(lanes[lanes.length - 1], items) ? 1 : 0;
      const laneBuffersHeight = (numberOfSignalLanes - extraBuffer) * this.getLaneBuffer(isCapsuleTime);

      return (displayHeight - laneBuffersHeight - this.totalConditionLaneHeight) / numberOfSignalLanes;
    }

    return displayHeight - this.totalConditionLaneHeight;
  };

  /**
   * Calculates the y-offset and height for each lane
   */
  computeLaneValues = (lane: number, options: ChartLaneOptions): LaneValues => {
    const { items, isCapsuleTime, capsuleLaneHeight } = options;
    const buffer = this.getLaneBuffer(isCapsuleTime);

    let offset = 0;
    for (let i = 1; i < lane; i++) {
      const conditionLaneHeight = this.conditionLaneHeights.get(i)?.height;
      if (conditionLaneHeight) {
        offset += conditionLaneHeight + this._extraConditionLaneHeight;
      }
    }

    const signalLaneHeight = this.getSignalLaneHeight(options);
    const signalLanes = this.getDisplayedLanes(items, true);
    _.some(signalLanes, (currentLane) => {
      if (currentLane >= lane) {
        return true;
      }

      offset += signalLaneHeight + buffer;
    });

    offset += capsuleLaneHeight;

    const conditionLane = this.conditionLaneHeights.get(lane);
    const conditionLaneHeight = conditionLane ? conditionLane.height + this._extraConditionLaneHeight : undefined;
    const hasSignalsInThisLane = this.hasSignalsInLane(lane, items);
    const isMixedLane = conditionLaneHeight !== undefined && hasSignalsInThisLane;

    const height = isMixedLane ? conditionLaneHeight + signalLaneHeight : (conditionLaneHeight ?? signalLaneHeight);

    return {
      offset,
      height,
      buffer,
      conditionHeight: isMixedLane ? conditionLaneHeight : undefined,
      signalHeight: isMixedLane ? signalLaneHeight : undefined,
    };
  };

  extraTopOffset = (rowOffset: number, extraTopBuffer: number) => (rowOffset > 0 ? 0 : extraTopBuffer);

  extraRowOffset = (lane: number, rowOffset: number) => {
    const laneData = this.allLaneData.get(lane);
    if (laneData) {
      return (laneData.rowExtraHeight ?? 0) * [...laneData.capsulesMap.keys()].indexOf(rowOffset);
    }

    return 0;
  };

  updateConditionOffsets = (
    conditionAxisAndOffsets: ConditionAxisAndOffsets,
    options: ChartLaneOptions,
    extraTopBuffers: Map<number, number>,
  ): void => {
    conditionAxisAndOffsets.forEach(
      ({ axis, rowOffset, lane, overlay = 1, orderInLane, conditionsWithoutCapsulesCount = 0, overlayLineWidth }) => {
        const { offset } = chartLanes.computeLaneValues(lane, options);
        const overlayOffset = getOverlayOffset(overlay, conditionsWithoutCapsulesCount, overlayLineWidth);
        const hasOrderInLane = _.isFinite(orderInLane);
        const extraTopOff = this.extraTopOffset(rowOffset, extraTopBuffers.get(lane)!);
        const extraRowOff = hasOrderInLane ? 0 : this.extraRowOffset(lane, rowOffset);
        const top = offset + rowOffset + extraTopOff + extraRowOff + overlayOffset;

        axis.update({ top }, false);
      },
    );
  };

  hasNoSignal = (items: any[]): boolean =>
    !items.some(({ itemType }) => itemType === ITEM_TYPES.SERIES || itemType === ITEM_TYPES.SCALAR);

  computeConditionLaneExtraHeight = (options: ChartLaneOptions) => {
    const { items, chart } = options;
    if (this.hasNoSignal(items)) {
      const heightDelta = chart.plotHeight - this.totalConditionLaneHeight;
      if (heightDelta < EXPAND_THRESHOLD) {
        return 0;
      }

      return heightDelta / this.getDisplayedLanes(items).length;
    }

    return 0;
  };

  private computeExtraCapsuleHeight = (lane: number, extraHeight: number, numCapsuleRows: number): number => {
    let numOffsets = 0;
    this.allLaneData.get(lane)?.capsulesMap.forEach((capsules) => {
      numOffsets += _.uniqBy(capsules, 'yValue').length;
    });

    return extraHeight / Math.max(numOffsets, numCapsuleRows);
  };

  updateAdjustableConditions = (conditions: AdjustableConditions, shouldExpandCapsules: boolean): void => {
    if (shouldExpandCapsules) {
      this.conditionsInSignalLane = conditions;
    } else {
      this.expandedConditions = conditions;
    }
  };

  /**
   * Returns extra condition lane height such that the total condition lane height
   * is below half of the chart height
   */
  restrictExtraConditionLaneHeight = (
    extraHeight: number,
    conditions: AdjustableConditions,
    options: ChartLaneOptions,
  ): number => {
    if (conditions.size > 1 || !extraHeight) {
      return extraHeight;
    }

    const [firstCondition] = conditions;
    const lane = firstCondition[1].lane;
    const halfChartHeight = this.getChartSeriesDisplayHeight(options) / 2;
    const currentLaneHeight = this.conditionLaneHeights.get(lane)?.height ?? 0;
    if (currentLaneHeight >= halfChartHeight) {
      return 0;
    }

    const newLaneHeight = currentLaneHeight + extraHeight;
    if (newLaneHeight <= halfChartHeight) {
      return extraHeight;
    }

    return newLaneHeight - halfChartHeight;
  };

  adjustSeriesData = (series: Highcharts.Series, yAdjustmentFunction: (yValue: number) => number) =>
    _.map(_.get(series, 'userOptions.data') ?? [], (d) =>
      _.has(d, 'x') && _.has(d, 'y') ? { ...d, x: d.x, y: yAdjustmentFunction(d.y) } : d,
    );

  adjustCapsulesLineWidth = (conditions: AdjustableConditions, options: ChartLaneOptions, extraHeight = 0): void => {
    if (conditions.size === 0) {
      return;
    }

    const { chart } = options;
    const restrictedExtraHeight = this.restrictExtraConditionLaneHeight(extraHeight, conditions, options);
    const shouldExpandCapsules = restrictedExtraHeight > 0;

    this.updateAdjustableConditions(conditions, shouldExpandCapsules);

    const labelHeight = getScaledFontSize(LANE_LABEL_HEIGHT_REM);
    const maxOverlayByLane = new Map<number, number>();
    const overlayConditionCountByLane = new Map<number, number>();
    conditions.forEach(({ lane, overlay }) => {
      if (_.isFinite(overlay)) {
        maxOverlayByLane.set(lane, Math.max(maxOverlayByLane.get(lane) ?? 0, overlay as number));
        overlayConditionCountByLane.set(lane, (overlayConditionCountByLane.get(lane) ?? 0) + 1);
      }
    });

    let capsuleLineWidth = shouldExpandCapsules ? 0 : this.getSignalLaneHeight(options);
    conditions.forEach(
      (
        {
          maxYValue,
          minYValue,
          lane,
          axis,
          rowOffset,
          lineWidth = 1,
          numCapsuleRows = 1,
          overlay = 1,
          orderInLane,
          conditionsWithoutCapsulesCount = 0,
        },
        conditionId,
      ) => {
        let extraCapsuleHeight = 0;
        if (shouldExpandCapsules) {
          extraCapsuleHeight = this.computeExtraCapsuleHeight(lane, restrictedExtraHeight, numCapsuleRows);
          capsuleLineWidth = lineWidth + extraCapsuleHeight;
          const laneData = this.allLaneData.get(lane);
          if (laneData) {
            laneData.rowExtraHeight = extraCapsuleHeight;
          }
          this._extraConditionLaneHeight = restrictedExtraHeight;
        }

        const allSeries = _.filter(
          chart.series,
          (s) => getCertainId(_.get(s, 'userOptions.conditionId')) === conditionId,
        );

        let extraRangeAdjustment = 0;
        const laneCapsules = Array.from(this.allLaneData.get(lane)?.capsulesMap ?? []);
        for (let i = 0; i < laneCapsules.length; i++) {
          const [offset, capsules] = laneCapsules[i];
          if (offset === rowOffset) {
            break;
          }

          extraRangeAdjustment += extraCapsuleHeight * (_.uniqBy(capsules, 'yValue').length - 1);
        }

        const { offset, conditionHeight, signalHeight } = this.computeLaneValues(lane, options);
        const signalAreaHeight = signalHeight ?? this.getSignalLaneHeight(options);
        const effectiveOffset = conditionHeight ? offset + conditionHeight : offset;
        const availableSignalHeight = signalAreaHeight - labelHeight;

        const hasOrderInLane = _.isFinite(orderInLane);
        const useOverlayLayout =
          !shouldExpandCapsules && _.isFinite(overlay) && (overlayConditionCountByLane.get(lane) ?? 0) > 1;

        let capsuleLineWidthForCondition = shouldExpandCapsules ? capsuleLineWidth : signalAreaHeight;
        let buffer = capsuleLineWidthForCondition / 2;
        let min = minYValue - buffer - extraRangeAdjustment;
        let max = maxYValue + buffer + extraRangeAdjustment + extraCapsuleHeight * (numCapsuleRows - 1);
        let height = max - min;
        let top = hasOrderInLane ? effectiveOffset + rowOffset : effectiveOffset;

        if (useOverlayLayout) {
          const { capsuleHeight, capsuleTopInset } = computeOverlayLayoutForLevel({
            heightPerLevel: availableSignalHeight,
            overlay: overlay as number,
            maxOverlayInRow: maxOverlayByLane.get(lane) ?? (overlay as number),
            conditionsWithoutCapsulesCount,
          });
          capsuleLineWidthForCondition = capsuleHeight;
          buffer = capsuleHeight / 2;
          min = -buffer;
          max = buffer;
          height = capsuleHeight;
          top = effectiveOffset + labelHeight + capsuleTopInset;
        }

        axis.update(
          {
            height,
            top,
            min,
            max,
          },
          false,
        );

        _.chain(allSeries)
          .groupBy((s) => s.dataTable?.columns?.y[0])
          .values()
          .sortBy((s) => s[0].dataTable?.columns?.y[0])
          .forEach((seriesArray, index) => {
            _.forEach(seriesArray, (series) => {
              const color = series.color && series.color.toString();
              const seriesOptions = {
                lineWidth:
                  series.userOptions?.itemType === ITEM_TYPES.CAPSULE
                    ? capsuleLineWidthForCondition
                    : 0.9 * capsuleLineWidthForCondition,
              } as SeriesLineOptions;

              if (extraCapsuleHeight) {
                if (allSeries.length > 1) {
                  const newData = this.adjustSeriesData(series, (yValue) =>
                    yValue > 1 ? +index * capsuleLineWidth : yValue,
                  );
                  series.setData(newData, false, false, false);
                }
              } else if (!shouldExpandCapsules) {
                seriesOptions.color = tinycolor(color).setAlpha(0.3).toString();

                if (allSeries.length > 1) {
                  const newData = this.adjustSeriesData(series, (yValue) => (!yValue ? yValue : 1));
                  series.setData(newData, false, false, false);
                }
              } else if (_.get(series, 'userOptions.dim')) {
                seriesOptions.color = tinycolor(color).setAlpha(0.2).toString();
              }

              if (overlay) {
                // Stretched into the signal lane: stay behind the signal (zIndex 1). Wedding-cake overlays keep
                // relative ordering between conditions (lower overlay index = further back).
                const maxOverlayInLane = maxOverlayByLane.get(lane) ?? (overlay as number);
                seriesOptions.zIndex = useOverlayLayout ? -(maxOverlayInLane - (overlay as number) + 1) : 0;
              }

              series.update(seriesOptions, false);
            });
          })
          .value();
      },
    );
  };

  getAdjustedConditions = (conditionId: string): AdjustableConditions | undefined => {
    if (this.conditionsInSignalLane.has(conditionId)) {
      return this.conditionsInSignalLane;
    }

    if (this.expandedConditions.has(conditionId)) {
      return this.expandedConditions;
    }
  };

  getLaneFromYPos = (yPos: number, options: ChartLaneOptions): number => {
    const { items, isCapsuleTime } = options;
    let currentPos = 0;
    const lanes = this.getDisplayedLanes(items);
    const signalLaneHeight = this.getSignalLaneHeight(options);
    const laneBuffer = this.getLaneBuffer(isCapsuleTime);
    for (let i = 0; i < lanes.length; i++) {
      const lane = lanes[i];
      const conditionLaneHeight = this.conditionLaneHeights.get(lane)?.height;
      currentPos += conditionLaneHeight ? conditionLaneHeight + this.extraConditionLaneHeight : signalLaneHeight;
      if (currentPos > yPos) {
        return lane;
      }

      currentPos += this.conditionLaneHeights.has(lane) ? 0 : laneBuffer;
    }

    return lanes[0];
  };

  resetExtraConditionLaneHeight = () => {
    this._extraConditionLaneHeight = 0;
    this.allLaneData.forEach((laneData) => {
      if (laneData.rowExtraHeight) {
        laneData.rowExtraHeight = 0;
      }
    });
  };

  /**
   * Tiny helper function to determine the correct lane buffer to use
   *
   * @param {Boolean} isCapsuleTime - Flag indicating if capsule time is displayed
   */
  getLaneBuffer = (isCapsuleTime: boolean) => (isCapsuleTime ? CAPSULE_TIME_LANE_BUFFER : LANE_BUFFER);

  hasSignalsInLane = (lane: number | undefined, items: readonly any[]): boolean => {
    if (!lane) {
      return false;
    }

    return _.chain(items)
      .filter({ lane })
      .some(({ itemType }) => itemType === ITEM_TYPES.SERIES || itemType === ITEM_TYPES.SCALAR)
      .value();
  };

  getDisplayedLanes = (items: any[], excludeCapsules = false): number[] =>
    _.chain(items)
      .reject(({ itemType }) => excludeCapsules && _.includes([...CAPSULE_TYPES, ITEM_TYPES.CONDITION], itemType))
      .map('lane')
      .compact()
      .uniq()
      .sortBy()
      .value();

  getNumberOfDisplayedLanes = (items: any[]) => this.getDisplayedLanes(items).length;

  private getLaneLabelBuffer = (lane: number, extraTopBuffers: Map<number, number>): number =>
    extraTopBuffers.get(lane) ?? extraTopBuffers.get(-1) ?? 0;

  private getConditionsInSignalLaneStackHeight = (
    lane: number,
    conditionsInSignalLane: AdjustableConditions,
  ): number => {
    let maxStackHeight = 0;
    conditionsInSignalLane.forEach((condition) => {
      if (condition.lane === lane) {
        maxStackHeight = Math.max(maxStackHeight, condition.maxYValue - condition.minYValue);
      }
    });

    return maxStackHeight;
  };

  /**
   * Sum of natural heights for conditions, signal lane labels, capsule stacks in signal lanes, and lane buffers.
   */
  computeRequiredDisplayHeight = (
    allLaneData: ConditionLaneData,
    items: any[],
    options: Pick<ChartLaneOptions, 'chart' | 'capsuleLaneHeight' | 'isCapsuleTime' | 'items'>,
    conditionsInSignalLane: AdjustableConditions,
    extraTopBuffers: Map<number, number>,
  ): number => {
    const displayedLanes = this.getDisplayedLanes(items);
    const signalLanes = this.getDisplayedLanes(items, true);
    const extraBuffer = this.hasSignalsInLane(displayedLanes[displayedLanes.length - 1], items) ? 1 : 0;
    const laneBuffer = this.getLaneBuffer(options.isCapsuleTime);

    let required = Math.max(0, signalLanes.length - extraBuffer) * laneBuffer;

    const lanesWithConditionBand = [...allLaneData.keys()];

    required += lanesWithConditionBand.reduce(
      (sum, lane) =>
        sum +
        (allLaneData.get(lane)?.nextOffset ?? 0) +
        (this.hasSignalsInLane(lane, items) ? this.getLaneLabelBuffer(lane, extraTopBuffers) : 0),
      0,
    );

    required += signalLanes
      .filter((lane) => !lanesWithConditionBand.includes(lane))
      .reduce(
        (sum, lane) =>
          sum +
          this.getLaneLabelBuffer(lane, extraTopBuffers) +
          this.getConditionsInSignalLaneStackHeight(lane, conditionsInSignalLane),
        0,
      );

    return required;
  };

  /**
   * Max height per condition lane when the chart is shorter than the natural layout.
   * Returns 0 when conditions, signals, and offsets fit without compression.
   */
  getAllocatedLaneHeight = (
    allLaneData: ConditionLaneData,
    items: any[],
    options: Pick<ChartLaneOptions, 'chart' | 'capsuleLaneHeight' | 'isCapsuleTime' | 'items'>,
    conditionsInSignalLane: AdjustableConditions,
    extraTopBuffers: Map<number, number>,
  ): number => {
    const displayHeight = this.getChartSeriesDisplayHeight(options);
    const lanesWithConditions = [...allLaneData.keys()];
    const lanesWithSignals = this.getDisplayedLanes(items, true);
    const conditionOnlyLaneCount = lanesWithConditions.filter((lane) => !this.hasSignalsInLane(lane, items)).length;
    const mixedLaneCount = lanesWithConditions.filter((lane) => this.hasSignalsInLane(lane, items)).length;
    const signalOnlyLaneCount = lanesWithSignals.filter((lane) => !lanesWithConditions.includes(lane)).length;
    const lanesSharingHeight = conditionOnlyLaneCount + mixedLaneCount * 2 + signalOnlyLaneCount;

    if (lanesSharingHeight === 0) {
      return 0;
    }

    const requiredHeight = this.computeRequiredDisplayHeight(
      allLaneData,
      items,
      options,
      conditionsInSignalLane,
      extraTopBuffers,
    );

    if (requiredHeight <= displayHeight) {
      return 0;
    }

    return displayHeight / lanesSharingHeight;
  };

  /**
   * Tiny helper to return the appropriate height for everything that should not overlap the capsule lane.
   *
   * @returns the height in pixel that is available
   */
  getChartSeriesDisplayHeight = (options: Pick<ChartLaneOptions, 'chart' | 'capsuleLaneHeight'>): number =>
    options.chart.plotHeight - options.capsuleLaneHeight;
}

export const chartLanes = new ChartLanes();
